/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 *
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.4.0
 * @since 1.0.0
 */

import { Formik, FormikErrors, FormikProps } from 'formik';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { people } from '../../../../assets/icons';
import { BlockWithIcon, Button, PasswordInput } from '../../../../components/common';
import { LayoutStyled } from '../../../../components/common/Block/Layout';
import { EInputKind } from '../../../../components/common/Input/Input';
import IPageAuthTranslation from '../../../../modules/internationalization/types/pages/auth';
import { openNotification } from '../../../../modules/notification/notificationActions';
import { ENotificationLevelTypes, INotification } from '../../../../modules/notification/types/notificationTypes';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { submitChangePasswordFormStart } from '../../actions/changePasswordActions';
import { makeSelectChangePassword } from '../../selectors/changePasswordSelectors';
import {
    FormContainerStyled,
    FormGroupStyled,
    FormHelpStyled,
    FormTitleStyled,
    FormWrapperStyled,
} from '../../styled/PageAuthStyled';
import { IChangePasswordFormPayload, IChangePasswordState } from '../../types/changePasswordTypes';
import { passwordValidation } from './changePasswordValidation';

type TChangePassword = IChangePasswordProps & IConnectedProps & IDispatchProps;

export const ChangePassword = (props: TChangePassword) => {
    const minLength = props.validationParams.minLength;
    const maxLength = props.validationParams.maxLength;

    const handleSubmit = (values: IChangePasswordFormPayload) => {
        if (minLength && maxLength) {
            props.submitForm(values);
        }
    };

    const handleClick = (errors: FormikErrors<IChangePasswordFormPayload>) => {
        const error = Object.values(errors)[0];
        if (error) {
            props.openNotification({
                level: ENotificationLevelTypes.ERROR,
                message: error,
            });
        }
    };

    return (
        <FormContainerStyled>
            <Formik
                initialValues={formInitialValues}
                render={(formikBag: FormikProps<IChangePasswordFormPayload>) => (
                    <FormWrapperStyled
                        className="login-form"
                        onSubmit={formikBag.handleSubmit}
                    >
                        <FormTitleStyled>{props.translation.changePasswordForm.title}</FormTitleStyled>

                        <LayoutStyled className="mb-2">
                            <FormHelpStyled>
                                {props.translation.changePasswordForm.subTitle}
                            </FormHelpStyled>
                        </LayoutStyled>

                        <LayoutStyled marginBottom="29px">
                            <FormHelpStyled>
                                {minLength && maxLength &&
                                    props.translation.changePasswordForm.help
                                        .replace('{min}', minLength.toString())
                                        .replace('{max}', maxLength.toString())}
                            </FormHelpStyled>
                        </LayoutStyled>

                        <FormGroupStyled className="form-group__password-input">
                            <PasswordInput
                                data-input-pwd="changePasswordFormPassword1"
                                kind={EInputKind.normal}
                                label={props.translation.changePasswordForm.inputLabel1}
                                name="password"
                                value={formikBag.values.password}
                                onBlur={formikBag.handleBlur}
                                onChange={formikBag.handleChange}
                            />
                        </FormGroupStyled>
                        <FormGroupStyled className="form-group__password-input">
                            <PasswordInput
                                data-input-pwd="changePasswordFormPassword2"
                                kind={EInputKind.normal}
                                label={props.translation.changePasswordForm.inputLabel2}
                                name="passwordConfirm"
                                value={formikBag.values.passwordConfirm}
                                onBlur={formikBag.handleBlur}
                                onChange={formikBag.handleChange}
                            />
                        </FormGroupStyled>
                        <Button
                            data-button-submit="buttonSubmitSuthform"
                            disabled={!(minLength && maxLength)}
                            kind="medium"
                            type="submit"
                            onClick={() => handleClick(formikBag.errors)}
                        >
                            {props.translation.changePasswordForm.buttonSubmit}
                        </Button>
                    </FormWrapperStyled>
                )}
                validate={passwordValidation(props.translation, props.validationParams)}
                onSubmit={handleSubmit}
            >
            </Formik>
            <BlockWithIcon
                data-text-with-icon="howToGetAccess"
                iconRight={people}
                iconRightSize={64}
                justifyContent="flex-end"
            />
        </FormContainerStyled>
    );
};

const formInitialValues: IChangePasswordFormPayload = {
    password: '',
    passwordConfirm: '',
};

export interface IChangePasswordProps {
    translation: IPageAuthTranslation;
}

export interface IConnectedProps {
    validationParams: IChangePasswordState;
}

export interface IDispatchProps {
    openNotification: (notification: INotification) => void;
    submitForm: (values: IChangePasswordFormPayload) => void;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    validationParams: makeSelectChangePassword(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    openNotification: (payload: INotification) => dispatch(openNotification(payload)),
    submitForm: (payload: IChangePasswordFormPayload) => dispatch(submitChangePasswordFormStart(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChangePassword as any) as any;
